export default `<p><strong>Document Version:</strong> V3.28 &nbsp;|&nbsp; <strong>Release Date:</strong> 2022 &nbsp;|&nbsp; <strong>Document Type:</strong> Product Whitepaper &amp; Security Assessment Summary</p>

<h2>1. Executive Summary</h2>
<p>This Lending Pool module is an on-chain liquidity lending service built on decentralized finance (DeFi) money market protocols. Through open-source smart contracts and programmatic interest rate models, the system connects users' digital assets to established on-chain lending markets, enabling <strong>yield on deposits, collateralized borrowing, and shared liquidity</strong>.</p>
<p>The module does not rely on manual intervention for day-to-day fund allocation. Key metrics such as interest rates, capacity limits, and utilization are determined in real time by on-chain markets and displayed transparently via multi-source data aggregation and scheduled synchronization.</p>

<h2>2. Industry Background &amp; Product Positioning</h2>
<p>Traditional lending depends on centralized institutions for credit assessment and fund matching. DeFi lending protocols use smart contracts to automatically match supply and demand, with over-collateralization and liquidation mechanisms to protect system solvency.</p>
<p>This Lending Pool module is positioned as:</p>
<ul>
  <li><strong>On-chain money market gateway:</strong> Aggregates lending pool data across multiple chains and protocols;</li>
  <li><strong>Programmatic yield distribution:</strong> Automatically settles earnings on a fixed schedule based on authorized balances and market APY;</li>
  <li><strong>Transparent &amp; verifiable:</strong> Pool TVL, APY, utilization, and LTV metrics are traceable to public on-chain data;</li>
  <li><strong>Non-custodial by design:</strong> Asset authorization and on-chain interactions are initiated from the user's wallet.</li>
</ul>

<h2>3. System Architecture</h2>
<h3>3.1 Architecture Overview</h3>
<ul>
  <li><strong>User interface layer:</strong> Web/mobile UI for wallet connection, authorization, pool browsing, and earnings queries;</li>
  <li><strong>Business service layer:</strong> Authentication, authorization management, yield order generation, and multilingual content;</li>
  <li><strong>Data aggregation layer:</strong> Syncs lending pool metrics from public DeFi data sources on a schedule;</li>
  <li><strong>On-chain protocol layer:</strong> Audited open-source lending protocol smart contracts execute deposit and borrow logic.</li>
</ul>

<h3>3.2 Core Metrics</h3>
<table>
  <thead>
    <tr><th>Metric</th><th>Description</th></tr>
  </thead>
  <tbody>
    <tr><td>TVL</td><td>Total pool assets in USD; reflects market depth</td></tr>
    <tr><td>Supply APY</td><td>Deposit annual yield from base rate plus protocol rewards</td></tr>
    <tr><td>Borrow APY</td><td>Annual cost paid by borrowers</td></tr>
    <tr><td>Utilization Rate</td><td>Ratio of borrowed to available funds</td></tr>
    <tr><td>Max LTV</td><td>Maximum loan-to-value ratio before liquidation</td></tr>
    <tr><td>Supply / Borrow Cap</td><td>Per-asset limits to control concentration risk</td></tr>
    <tr><td>Available Liquidity</td><td>Liquidity available for immediate borrowing</td></tr>
  </tbody>
</table>

<h2>4. User Flow &amp; Yield Mechanics</h2>
<ol>
  <li><strong>Connect wallet</strong> via Web3 wallet;</li>
  <li><strong>Authorize assets</strong> (USDT, USDC, etc.) with on-chain Approve;</li>
  <li><strong>Join the lending pool</strong> to enter the yield calculation queue;</li>
  <li><strong>Yield settlement</strong> once daily based on authorized balance and market rates;</li>
  <li><strong>View assets</strong> under My Portfolio for balances and settlement history.</li>
</ol>
<p><strong>Historical APY does not guarantee future returns.</strong></p>

<h3>4.3 Fees</h3>
<h4>How are DeFi Earn fees charged?</h4>
<p>On-chain transactions require gas fees to complete interactions with smart contracts, including subscription and yield claiming. For certain assets, the DeFi lending pool and vault managers may charge a performance fee as a percentage of your actual profits; your principal is not affected.</p>

<h2>5. Security Design</h2>
<ul>
  <li>Underlying protocols use audited open-source contracts with on-chain core logic;</li>
  <li>Non-custodial architecture; no private keys or seed phrases stored;</li>
  <li>Minimal Token Approve permissions; revocable anytime in the wallet;</li>
  <li>Token auth, HTTPS transport, RBAC admin access, and operation audit trails.</li>
</ul>

<h2>6. Security Assessment Summary</h2>
<table>
  <thead>
    <tr><th>Item</th><th>Result</th><th>Notes</th></tr>
  </thead>
  <tbody>
    <tr><td>Smart contract dependencies</td><td><strong>Pass</strong></td><td>Publicly audited DeFi protocol contracts</td></tr>
    <tr><td>Non-custodial &amp; key management</td><td><strong>Pass</strong></td><td>No private key storage</td></tr>
    <tr><td>API &amp; authentication</td><td><strong>Pass</strong></td><td>Token auth and HTTPS in place</td></tr>
    <tr><td>Data sync &amp; consistency</td><td><strong>Pass</strong></td><td>Scheduled sync with snapshots</td></tr>
    <tr><td>Yield settlement logic</td><td><strong>Pass</strong></td><td>Explicit rules; auditable orders</td></tr>
  </tbody>
</table>

<h2>7. Risk Disclosure</h2>
<ul>
  <li><strong>Smart contract risk:</strong> Undiscovered vulnerabilities may exist;</li>
  <li><strong>Market risk:</strong> APY can fluctuate significantly;</li>
  <li><strong>Liquidation risk:</strong> Collateral price drops may trigger liquidation;</li>
  <li><strong>Stablecoin de-peg risk:</strong> USDT/USDC may deviate from $1.00;</li>
  <li><strong>Regulatory risk:</strong> DeFi regulation is evolving globally.</li>
</ul>
<p><strong>This module does not provide investment advice and does not guarantee fixed returns.</strong></p>

<h2>8. Glossary</h2>
<ul>
  <li><strong>APY:</strong> Annual Percentage Yield;</li>
  <li><strong>TVL:</strong> Total Value Locked;</li>
  <li><strong>LTV:</strong> Loan-to-Value ratio;</li>
  <li><strong>Approve:</strong> ERC-20 token authorization;</li>
  <li><strong>Liquidation:</strong> Automatic collateral sale when debt is under-collateralized.</li>
</ul>

<p>— For informational purposes only. Not legal, financial, or investment advice. Verify on-chain data via block explorers and official protocol interfaces.</p>`
